import React, { useEffect, useRef, useState } from "react";
import { Redirect, Route, Switch, useHistory, useParams } from "react-router-dom";
import { io } from "socket.io-client";
import {
	Alert,
	Anchor,
	Badge,
	Box,
	Button,
	Checkbox,
	Container,
	Divider,
	FileInput,
	Group,
	List,
	Paper,
	Radio,
	SimpleGrid,
	Stack,
	Text,
	TextInput,
	Textarea,
	Title
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
	IconAlertCircle,
	IconBriefcase,
	IconCalendar,
	IconCheck,
	IconMapPin,
	IconMicrophone,
	IconMicrophoneOff,
	IconVideo,
	IconVideoOff
} from "@tabler/icons-react";
import config from "../../config";

const apiBase = `${config.api.API_URL}/${config.api.API_Version}/public/careers`;
const socketUrl = config.api.API_URL || window.location.origin;

function ml(value) {
	if (!value) return "";
	if (typeof value === "string") return value;
	return value.en || value.hy || value.ru || Object.values(value)[0] || "";
}

async function publicJson(path, options = {}) {
	const response = await fetch(`${apiBase}${path}`, {
		...options,
		headers: {
			...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
			...(options.headers || {})
		}
	});
	const data = await response.json().catch(() => ({}));
	if (!response.ok) {
		throw new Error(data.error || "Request failed");
	}
	return data;
}

function FieldPair({ children }) {
	return <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>{children}</SimpleGrid>;
}

function PublicShell({ children, narrow = false }) {
	return (
		<Box mih="100vh" bg="gray.0" py={{ base: 28, sm: 52 }}>
			<Container size={narrow ? "md" : "lg"}>
				<Stack spacing="xl">{children}</Stack>
			</Container>
		</Box>
	);
}

function MetaBadge({ icon, children }) {
	return (
		<Badge leftSection={icon} variant="light" color="gray" radius="sm" size="lg">
			{children}
		</Badge>
	);
}

function CareersList() {
	const [jobs, setJobs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	useEffect(() => {
		let mounted = true;
		publicJson("")
			.then((data) => {
				if (mounted) setJobs(Array.isArray(data) ? data : []);
			})
			.catch((err) => mounted && setError(err.message))
			.finally(() => mounted && setLoading(false));
		return () => {
			mounted = false;
		};
	}, []);

	return (
		<PublicShell>
			<Stack spacing={6}>
				<Badge color="teal" variant="filled" w="fit-content">APIT Careers</Badge>
				<Title order={1}>Open positions</Title>
				<Text color="dimmed" maw={720}>
					Join APIT and help build practical digital infrastructure with a team that values clarity,
					ownership, and useful technology.
				</Text>
			</Stack>
			{error ? <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert> : null}
			{loading ? <Text color="dimmed">Loading positions...</Text> : null}
			{!loading && !jobs.length ? (
				<Paper withBorder radius="sm" p="xl">
					<Text color="dimmed">There are no open positions right now.</Text>
				</Paper>
			) : null}
			<Stack spacing="md">
				{jobs.map((job) => (
					<Paper key={job._id} withBorder radius="sm" p="lg">
						<Group position="apart" align="flex-start" noWrap>
							<Stack spacing="xs">
								<Title order={3}>{ml(job.title)}</Title>
								<Group spacing="xs">
									<MetaBadge icon={<IconMapPin size={14} />}>{job.location || "Location TBD"}</MetaBadge>
									<MetaBadge icon={<IconBriefcase size={14} />}>{job.employmentType || "Full-time"}</MetaBadge>
									{job.deadline ? (
										<MetaBadge icon={<IconCalendar size={14} />}>
											Apply by {new Date(job.deadline).toLocaleDateString()}
										</MetaBadge>
									) : null}
								</Group>
							</Stack>
							<Button component="a" href={`/careers/${job.slug || job._id}`} radius="sm">
								View role
							</Button>
						</Group>
					</Paper>
				))}
			</Stack>
		</PublicShell>
	);
}

function JobDetails() {
	const { jobSlug } = useParams();
	const [job, setJob] = useState(null);
	const [error, setError] = useState("");

	useEffect(() => {
		let mounted = true;
		publicJson(`/${encodeURIComponent(jobSlug)}`)
			.then((data) => mounted && setJob(data))
			.catch((err) => mounted && setError(err.message))
			.finally(() => {});
		return () => {
			mounted = false;
		};
	}, [jobSlug]);

	if (error) {
		return (
			<PublicShell narrow>
				<Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert>
			</PublicShell>
		);
	}

	if (!job) {
		return (
			<PublicShell narrow>
				<Text color="dimmed">Loading position...</Text>
			</PublicShell>
		);
	}

	return (
		<PublicShell narrow>
			<Anchor href="/careers" color="dimmed">Back to careers</Anchor>
			<Paper withBorder radius="sm" p={{ base: "md", sm: "xl" }}>
				<Stack spacing="lg">
					<Stack spacing="xs">
						<Title order={1}>{ml(job.title)}</Title>
						<Group spacing="xs">
							<MetaBadge icon={<IconMapPin size={14} />}>{job.location || "Location TBD"}</MetaBadge>
							<MetaBadge icon={<IconBriefcase size={14} />}>{job.employmentType || "Full-time"}</MetaBadge>
						</Group>
					</Stack>
					{ml(job.description) ? <Text style={{ whiteSpace: "pre-wrap" }}>{ml(job.description)}</Text> : null}
					{Array.isArray(job.requirements) && job.requirements.length ? (
						<Stack spacing="xs">
							<Title order={3}>Requirements</Title>
							<List spacing="xs">{job.requirements.map((item, index) => <List.Item key={index}>{ml(item)}</List.Item>)}</List>
						</Stack>
					) : null}
					{Array.isArray(job.responsibilities) && job.responsibilities.length ? (
						<Stack spacing="xs">
							<Title order={3}>Responsibilities</Title>
							<List spacing="xs">{job.responsibilities.map((item, index) => <List.Item key={index}>{ml(item)}</List.Item>)}</List>
						</Stack>
					) : null}
					<Button component="a" href={`/careers/apply/${job._id}`} radius="sm" size="md">
						Apply for this position
					</Button>
				</Stack>
			</Paper>
		</PublicShell>
	);
}

function ApplyPage() {
	const { jobId } = useParams();
	const history = useHistory();
	const [job, setJob] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [cvFile, setCvFile] = useState(null);
	const [form, setForm] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		country: "",
		city: "",
		linkedin: "",
		github: "",
		portfolio: "",
		expectedSalary: "",
		availableStartDate: "",
		coverLetter: "",
		skills: "",
		languages: "",
		consentAccepted: false
	});

	useEffect(() => {
		let mounted = true;
		publicJson(`/${encodeURIComponent(jobId)}`)
			.then((data) => mounted && setJob(data))
			.catch((err) => mounted && setError(err.message));
		return () => {
			mounted = false;
		};
	}, [jobId]);

	function update(field, value) {
		setForm((current) => ({ ...current, [field]: value }));
	}

	async function uploadCV() {
		if (!cvFile) return null;
		const body = new FormData();
		body.append("file", cvFile);
		const response = await publicJson("/upload-cv", { method: "POST", body });
		return response.file || null;
	}

	async function submit() {
		setError("");
		if (!form.firstName || !form.lastName || !form.email) {
			setError("First name, last name, and email are required.");
			return;
		}
		if (!form.consentAccepted) {
			setError("Please accept the data processing consent.");
			return;
		}
		try {
			setLoading(true);
			const cv = await uploadCV();
			const result = await publicJson("/apply", {
				method: "POST",
				body: JSON.stringify({
					...form,
					jobId,
					expectedSalary: form.expectedSalary ? Number(form.expectedSalary) : undefined,
					source: "career_page",
					skills: form.skills.split(",").map((item) => item.trim()).filter(Boolean),
					languages: form.languages.split(",").map((item) => item.trim()).filter(Boolean),
					files: cv ? [cv] : []
				})
			});
			history.push(`/careers/application/${result.publicToken}`);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}

	return (
		<PublicShell narrow>
			<Anchor href={job?.slug ? `/careers/${job.slug}` : "/careers"} color="dimmed">Back to position</Anchor>
			<Paper withBorder radius="sm" p={{ base: "md", sm: "xl" }}>
				<Stack spacing="lg">
					<Stack spacing={4}>
						<Title order={1}>Apply for position</Title>
						<Text color="dimmed">{job ? ml(job.title) : "Loading position..."}</Text>
					</Stack>
					{error ? <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert> : null}
					<FieldPair>
						<TextInput label="First name" value={form.firstName} onChange={(e) => update("firstName", e.currentTarget.value)} required />
						<TextInput label="Last name" value={form.lastName} onChange={(e) => update("lastName", e.currentTarget.value)} required />
					</FieldPair>
					<FieldPair>
						<TextInput label="Email" type="email" value={form.email} onChange={(e) => update("email", e.currentTarget.value)} required />
						<TextInput label="Phone" value={form.phone} onChange={(e) => update("phone", e.currentTarget.value)} />
					</FieldPair>
					<FieldPair>
						<TextInput label="Country" value={form.country} onChange={(e) => update("country", e.currentTarget.value)} />
						<TextInput label="City" value={form.city} onChange={(e) => update("city", e.currentTarget.value)} />
					</FieldPair>
					<FieldPair>
						<TextInput label="LinkedIn" value={form.linkedin} onChange={(e) => update("linkedin", e.currentTarget.value)} />
						<TextInput label="Portfolio" value={form.portfolio} onChange={(e) => update("portfolio", e.currentTarget.value)} />
					</FieldPair>
					<FieldPair>
						<TextInput label="GitHub" value={form.github} onChange={(e) => update("github", e.currentTarget.value)} />
						<TextInput label="Expected salary" type="number" value={form.expectedSalary} onChange={(e) => update("expectedSalary", e.currentTarget.value)} />
					</FieldPair>
					<TextInput label="Available start date" type="date" value={form.availableStartDate} onChange={(e) => update("availableStartDate", e.currentTarget.value)} />
					<TextInput label="Skills" description="Separate skills with commas" value={form.skills} onChange={(e) => update("skills", e.currentTarget.value)} />
					<TextInput label="Languages" description="Separate languages with commas" value={form.languages} onChange={(e) => update("languages", e.currentTarget.value)} />
					<FileInput label="CV" placeholder="PDF, DOC, DOCX, or TXT" value={cvFile} onChange={setCvFile} accept=".pdf,.doc,.docx,.txt" />
					<Textarea label="Cover letter" minRows={5} value={form.coverLetter} onChange={(e) => update("coverLetter", e.currentTarget.value)} />
					<Checkbox
						checked={form.consentAccepted}
						onChange={(e) => update("consentAccepted", e.currentTarget.checked)}
						label="I consent to APIT processing my application data for recruitment purposes."
					/>
					<Button onClick={submit} loading={loading} radius="sm" disabled={!job}>
						Submit application
					</Button>
				</Stack>
			</Paper>
		</PublicShell>
	);
}

function ApplicationStatus() {
	const { token } = useParams();
	const [application, setApplication] = useState(null);
	const [error, setError] = useState("");

	useEffect(() => {
		let mounted = true;
		publicJson(`/application/${encodeURIComponent(token)}`)
			.then((data) => mounted && setApplication(data))
			.catch((err) => mounted && setError(err.message));
		return () => {
			mounted = false;
		};
	}, [token]);

	return (
		<PublicShell narrow>
			<Paper withBorder radius="sm" p={{ base: "md", sm: "xl" }}>
				<Stack spacing="lg">
					<Title order={1}>Application status</Title>
					{error ? <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert> : null}
					{!application && !error ? <Text color="dimmed">Loading application...</Text> : null}
					{application ? (
						<>
							<Group spacing="xs">
								<Badge color="blue" radius="sm">{application.pipelineStage}</Badge>
								<Badge color="green" radius="sm">{application.status}</Badge>
							</Group>
							<Text>
								{application.candidate?.firstName} {application.candidate?.lastName} for {ml(application.jobPosting?.title)}
							</Text>
							<Divider />
							<Stack spacing="xs">
								<Title order={3}>Timeline</Title>
								{(application.history || []).map((entry, index) => (
									<Paper key={index} withBorder radius="sm" p="sm">
										<Text fw={600}>{entry.action || entry.toStage || "Update"}</Text>
										<Text size="sm" color="dimmed">{entry.date ? new Date(entry.date).toLocaleString() : ""}</Text>
										{entry.comment ? <Text size="sm">{entry.comment}</Text> : null}
									</Paper>
								))}
							</Stack>
						</>
					) : null}
				</Stack>
			</Paper>
		</PublicShell>
	);
}

function InterviewPage() {
	const { token } = useParams();
	const [interview, setInterview] = useState(null);
	const [error, setError] = useState("");

	useEffect(() => {
		let mounted = true;
		publicJson(`/interview/${encodeURIComponent(token)}`)
			.then((data) => mounted && setInterview(data))
			.catch((err) => mounted && setError(err.message));
		return () => {
			mounted = false;
		};
	}, [token]);

	return (
		<PublicShell narrow>
			<Paper withBorder radius="sm" p={{ base: "md", sm: "xl" }}>
				<Stack spacing="lg">
					<Title order={1}>Interview</Title>
					{error ? <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert> : null}
					{!interview && !error ? <Text color="dimmed">Loading interview...</Text> : null}
					{interview ? (
						<>
							<Text fw={600}>{interview.title || "Recruitment interview"}</Text>
							<Text color="dimmed">{interview.jobTitle ? ml(interview.jobTitle) : ""}</Text>
							<Text>
								{new Date(interview.scheduledStart).toLocaleString()} - {new Date(interview.scheduledEnd).toLocaleTimeString()}
							</Text>
							{interview.status === "cancelled" ? <Alert color="red">This interview was cancelled.</Alert> : null}
							{interview.timeStatus === "not_yet" ? <Alert color="yellow">The meeting room is not open yet.</Alert> : null}
							{interview.timeStatus === "expired" ? <Alert color="gray">This interview link has expired.</Alert> : null}
							{interview.timeStatus === "active" ? (
								<Button component="a" href={`/careers/interview/${token}/meet`} radius="sm">
									Join APIT Meet
								</Button>
							) : null}
						</>
					) : null}
				</Stack>
			</Paper>
		</PublicShell>
	);
}

function AssessmentPage() {
	const { token } = useParams();
	const [assessment, setAssessment] = useState(null);
	const [answers, setAnswers] = useState({});
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	useEffect(() => {
		let mounted = true;
		publicJson(`/assessment/${encodeURIComponent(token)}`)
			.then((data) => mounted && setAssessment(data))
			.catch((err) => mounted && setError(err.message));
		return () => {
			mounted = false;
		};
	}, [token]);

	async function start() {
		try {
			const result = await publicJson(`/assessment/${encodeURIComponent(token)}/start`, { method: "POST", body: "{}" });
			setAssessment((current) => ({ ...current, status: result.status, startedAt: result.startedAt }));
		} catch (err) {
			setError(err.message);
		}
	}

	async function submit() {
		try {
			setLoading(true);
			await publicJson(`/assessment/${encodeURIComponent(token)}/submit`, {
				method: "POST",
				body: JSON.stringify({
					answers: Object.entries(answers).map(([questionIndex, answer]) => ({
						questionIndex: Number(questionIndex),
						answer
					}))
				})
			});
			setSubmitted(true);
			notifications.show({ color: "green", title: "Assessment submitted", message: "Thank you for completing the assessment." });
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}

	return (
		<PublicShell narrow>
			<Paper withBorder radius="sm" p={{ base: "md", sm: "xl" }}>
				<Stack spacing="lg">
					<Title order={1}>{assessment?.title || "Assessment"}</Title>
					{error ? <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert> : null}
					{!assessment && !error ? <Text color="dimmed">Loading assessment...</Text> : null}
					{assessment ? (
						<>
							<Text color="dimmed">{assessment.description}</Text>
							{assessment.timeLimitMinutes ? <Badge radius="sm">{assessment.timeLimitMinutes} minutes</Badge> : null}
							{assessment.status === "invited" ? <Button onClick={start} radius="sm">Start assessment</Button> : null}
							{assessment.status === "submitted" || submitted ? <Alert color="green" icon={<IconCheck size={18} />}>Assessment submitted.</Alert> : null}
							{assessment.status === "started" && !submitted ? (
								<Stack spacing="md">
									{(assessment.questions || []).map((question, index) => (
										<Paper key={index} withBorder radius="sm" p="md">
											<Stack spacing="xs">
												<Text fw={600}>{index + 1}. {question.question}</Text>
												{question.type === "multiple_choice" && Array.isArray(question.options) ? (
													<Radio.Group value={answers[index] || ""} onChange={(value) => setAnswers((current) => ({ ...current, [index]: value }))}>
														<Stack spacing={6}>
															{question.options.map((option) => <Radio key={option} value={option} label={option} />)}
														</Stack>
													</Radio.Group>
												) : (
													<Textarea minRows={3} value={answers[index] || ""} onChange={(e) => setAnswers((current) => ({ ...current, [index]: e.currentTarget.value }))} />
												)}
											</Stack>
										</Paper>
									))}
									<Button onClick={submit} loading={loading} radius="sm">Submit assessment</Button>
								</Stack>
							) : null}
						</>
					) : null}
				</Stack>
			</Paper>
		</PublicShell>
	);
}

function OfferPage() {
	const { token } = useParams();
	const [offer, setOffer] = useState(null);
	const [comment, setComment] = useState("");
	const [error, setError] = useState("");
	const [done, setDone] = useState("");

	useEffect(() => {
		let mounted = true;
		publicJson(`/offer/${encodeURIComponent(token)}`)
			.then((data) => mounted && setOffer(data))
			.catch((err) => mounted && setError(err.message));
		return () => {
			mounted = false;
		};
	}, [token]);

	async function respond(action) {
		try {
			await publicJson(`/offer/${encodeURIComponent(token)}/respond`, {
				method: "POST",
				body: JSON.stringify({ action, comment })
			});
			setDone(action === "accept" ? "Offer accepted." : "Offer rejected.");
		} catch (err) {
			setError(err.message);
		}
	}

	return (
		<PublicShell narrow>
			<Paper withBorder radius="sm" p={{ base: "md", sm: "xl" }}>
				<Stack spacing="lg">
					<Title order={1}>Offer</Title>
					{error ? <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert> : null}
					{done ? <Alert color="green" icon={<IconCheck size={18} />}>{done}</Alert> : null}
					{!offer && !error ? <Text color="dimmed">Loading offer...</Text> : null}
					{offer && !done ? (
						<>
							<Text fw={600}>{offer.jobTitle || offer.position}</Text>
							<Text color="dimmed">{offer.candidateName}</Text>
							<SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
								<Text>Salary: {offer.salary} {offer.currency}</Text>
								<Text>Start date: {offer.startDate ? new Date(offer.startDate).toLocaleDateString() : "TBD"}</Text>
								<Text>Contract: {offer.contractType || "TBD"}</Text>
								<Text>Probation: {offer.probationPeriod || "TBD"}</Text>
							</SimpleGrid>
							{Array.isArray(offer.benefits) && offer.benefits.length ? (
								<List>{offer.benefits.map((benefit, index) => <List.Item key={index}>{benefit}</List.Item>)}</List>
							) : null}
							<Textarea label="Comment" minRows={3} value={comment} onChange={(e) => setComment(e.currentTarget.value)} />
							<Group>
								<Button color="green" onClick={() => respond("accept")} radius="sm">Accept offer</Button>
								<Button color="red" variant="outline" onClick={() => respond("reject")} radius="sm">Reject offer</Button>
							</Group>
						</>
					) : null}
				</Stack>
			</Paper>
		</PublicShell>
	);
}

function GuestMeetPage() {
	const { token } = useParams();
	const [interview, setInterview] = useState(null);
	const [displayName, setDisplayName] = useState("Guest");
	const [joined, setJoined] = useState(false);
	const [error, setError] = useState("");
	const [muted, setMuted] = useState(false);
	const [cameraOff, setCameraOff] = useState(false);
	const socketRef = useRef(null);
	const streamRef = useRef(null);
	const peersRef = useRef(new Map());
	const localVideoRef = useRef(null);
	const remoteVideoRefs = useRef(new Map());
	const [remoteIds, setRemoteIds] = useState([]);

	useEffect(() => {
		let mounted = true;
		publicJson(`/interview/${encodeURIComponent(token)}`)
			.then((data) => mounted && setInterview(data))
			.catch((err) => mounted && setError(err.message));
		return () => {
			mounted = false;
			cleanupMeeting();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	function cleanupMeeting() {
		socketRef.current?.disconnect();
		streamRef.current?.getTracks().forEach((track) => track.stop());
		peersRef.current.forEach((peer) => peer.close());
		socketRef.current = null;
		streamRef.current = null;
		peersRef.current.clear();
	}

	function createPeer(remoteUserId, socket, initiator = false) {
		const peer = new RTCPeerConnection({ iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }] });
		streamRef.current?.getTracks().forEach((track) => peer.addTrack(track, streamRef.current));
		peer.ontrack = (event) => {
			setRemoteIds((current) => current.includes(remoteUserId) ? current : [...current, remoteUserId]);
			setTimeout(() => {
				const video = remoteVideoRefs.current.get(remoteUserId);
				if (video && event.streams[0]) video.srcObject = event.streams[0];
			}, 0);
		};
		peer.onicecandidate = (event) => {
			if (event.candidate) {
				socket.emit("meeting:ice-candidate", { roomId: interview.roomId, to: remoteUserId, candidate: event.candidate });
			}
		};
		peersRef.current.set(remoteUserId, peer);
		if (initiator) {
			peer.createOffer()
				.then((offer) => peer.setLocalDescription(offer).then(() => offer))
				.then((offer) => socket.emit("meeting:offer", { roomId: interview.roomId, to: remoteUserId, offer }));
		}
		return peer;
	}

	async function join() {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
			streamRef.current = stream;
			if (localVideoRef.current) localVideoRef.current.srcObject = stream;
			const socket = io(socketUrl, {
				query: { guestToken: token, displayName },
				transports: ["polling", "websocket"],
				reconnection: true
			});
			socketRef.current = socket;
			socket.on("connect", () => socket.emit("meeting:join", { roomId: interview.roomId, meetingId: interview.meetingId }));
			socket.on("meeting:user-joined", ({ userId }) => {
				if (userId !== socket.id && !peersRef.current.has(userId)) createPeer(userId, socket, true);
			});
			socket.on("meeting:user-left", ({ userId }) => {
				peersRef.current.get(userId)?.close();
				peersRef.current.delete(userId);
				setRemoteIds((current) => current.filter((id) => id !== userId));
			});
			socket.on("meeting:offer", async ({ from, offer }) => {
				const peer = peersRef.current.get(from) || createPeer(from, socket, false);
				await peer.setRemoteDescription(new RTCSessionDescription(offer));
				const answer = await peer.createAnswer();
				await peer.setLocalDescription(answer);
				socket.emit("meeting:answer", { roomId: interview.roomId, to: from, answer });
			});
			socket.on("meeting:answer", ({ from, answer }) => peersRef.current.get(from)?.setRemoteDescription(new RTCSessionDescription(answer)));
			socket.on("meeting:ice-candidate", ({ from, candidate }) => peersRef.current.get(from)?.addIceCandidate(new RTCIceCandidate(candidate)));
			socket.on("connect_error", (err) => setError(err.message));
			setJoined(true);
		} catch (err) {
			setError(`Unable to join meeting: ${err.message}`);
		}
	}

	function toggleMute() {
		streamRef.current?.getAudioTracks().forEach((track) => {
			track.enabled = muted;
		});
		setMuted((current) => !current);
	}

	function toggleCamera() {
		streamRef.current?.getVideoTracks().forEach((track) => {
			track.enabled = cameraOff;
		});
		setCameraOff((current) => !current);
	}

	const canJoin = interview?.timeStatus === "active" && interview?.roomId && interview?.meetingId;

	return (
		<PublicShell>
			<Paper withBorder radius="sm" p={{ base: "md", sm: "xl" }}>
				<Stack spacing="lg">
					<Title order={1}>APIT Meet</Title>
					{error ? <Alert color="red" icon={<IconAlertCircle size={18} />}>{error}</Alert> : null}
					{interview && !canJoin ? <Alert color="yellow">This meeting is not available right now.</Alert> : null}
					{!joined ? (
						<Stack maw={420}>
							<TextInput label="Display name" value={displayName} onChange={(e) => setDisplayName(e.currentTarget.value)} />
							<Button onClick={join} disabled={!canJoin} radius="sm">Join meeting</Button>
						</Stack>
					) : (
						<>
							<Group>
								<Button leftIcon={muted ? <IconMicrophoneOff size={18} /> : <IconMicrophone size={18} />} onClick={toggleMute} radius="sm">
									{muted ? "Unmute" : "Mute"}
								</Button>
								<Button leftIcon={cameraOff ? <IconVideoOff size={18} /> : <IconVideo size={18} />} onClick={toggleCamera} radius="sm">
									{cameraOff ? "Camera on" : "Camera off"}
								</Button>
								<Button color="red" variant="outline" onClick={() => { cleanupMeeting(); setJoined(false); }} radius="sm">
									Leave
								</Button>
							</Group>
							<SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
								<video ref={localVideoRef} autoPlay muted playsInline style={{ width: "100%", background: "#111", borderRadius: 4 }} />
								{remoteIds.map((id) => (
									<video key={id} ref={(node) => node && remoteVideoRefs.current.set(id, node)} autoPlay playsInline style={{ width: "100%", background: "#111", borderRadius: 4 }} />
								))}
							</SimpleGrid>
						</>
					)}
				</Stack>
			</Paper>
		</PublicShell>
	);
}

function PublicMeetRedirect() {
	const { token } = useParams();
	return <Redirect to={`/careers/interview/${token}/meet`} />;
}

export default function PublicCareers() {
	return (
		<Switch>
			<Route exact path="/careers" component={CareersList} />
			<Route exact path="/careers/apply/:jobId" component={ApplyPage} />
			<Route exact path="/careers/application/:token" component={ApplicationStatus} />
			<Route exact path="/careers/interview/:token" component={InterviewPage} />
			<Route exact path="/careers/interview/:token/meet" component={GuestMeetPage} />
			<Route exact path="/careers/assessment/:token" component={AssessmentPage} />
			<Route exact path="/careers/offer/:token" component={OfferPage} />
			<Route exact path="/public/meet/:token" component={PublicMeetRedirect} />
			<Route exact path="/careers/:jobSlug" component={JobDetails} />
		</Switch>
	);
}
